import React, { useState, useEffect } from 'react';
import { Switch, Tooltip, Modal } from 'antd';
import './LodestarTipsCard.css';

const LodestarTipsCard = ({ 
    initialConfig,
    onLodestarChange,
    disabled = false,
    tooltipText = "Use this toggle to apply Lodestar for all sections by default. You can still adjust each section individually below."
}) => {
    const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false);
    const [useLodestar, setUseLodestar] = useState(false);
    const providedByLodestar = [
        "C. Services you can shop for",
        "E. Taxes and government fees",
        "H. Other"
    ];
    
    const manualInputSections = [
        "B. Services you can't shop for",
        "F. Prepaid",
        "G. Initial Escrow payment at closing"
    ];
    const fieldsToCheck = [
        'enableCanShopServices',
        'enableTaxesAndOtherGovernmentFees',
        'enableOther'
    ];
    const hasAllFieldsEnabled = (config) => fieldsToCheck.every(field => config[field] === true);
    const hasAnyFieldEnabled = (config) => fieldsToCheck.some(field => config[field] === true);
    const hasMixedFields = (config) => hasAnyFieldEnabled(config) && !hasAllFieldsEnabled(config);
    const isMixed = hasMixedFields(initialConfig);

    // 监听initialConfig变化，更新开关状态
    useEffect(() => {
        setUseLodestar(hasAllFieldsEnabled(initialConfig));
    }, [initialConfig]);

    const handleSwitchChange = (checked) => {
        const nextChecked = isMixed ? true : checked;
        setUseLodestar(nextChecked);
        if (onLodestarChange) {
            onLodestarChange(nextChecked);
        }
    };

    return (
        <>
            <div className="estimatedclosingfee-tips-card">
                <div>
                    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="38" viewBox="0 0 36 38" fill="none">
                        <rect width="29.5962" height="33" rx="2" fill="#A4B7F8"/>
                        <rect x="3.40039" y="6" width="23.0192" height="3" rx="1.5" fill="white"/>
                        <rect x="3.40039" y="13" width="23.0192" height="3" rx="1.5" fill="white"/>
                        <rect x="3.40039" y="20" width="12.0577" height="3" rx="1.5" fill="white"/>
                        <rect x="19.4922" y="22.3936" width="16.5069" height="15.5185" rx="7.75926" fill="#1F3988"/>
                        <rect width="2.12425" height="6.37274" rx="1.06212" transform="matrix(0.728584 -0.684957 0.728584 0.684957 21.5547 30.24)" fill="white"/>
                        <rect width="2.12425" height="10.6212" rx="1.06212" transform="matrix(0.728584 0.684957 -0.728584 0.684957 32.3867 25.8755)" fill="white"/>
                    </svg>
                </div>
                <div className="estimatedclosingfee-tips-card-content">
                    <div className="estimatedclosingfee-tips-card-content-top">
                        <div className="estimatedclosingfee-tips-card-text">
                            <div className='estimatedclosingfee-tips-card-text-title'>{useLodestar ? 'Lodestar mode enabled' : isMixed ? 'Custom Lodestar mode' : 'Manual mode enabled'}</div>
                            <div className='estimatedclosingfee-tips-card-text-subtitle'>{useLodestar ? 'Lodestar will automatically provide fee data for most sections. You can still manually override fees below if needed.' : isMixed ? 'Lodestar is enabled for selected sections. You can adjust each section individually below.' : 'All fields are available for manual input.'}</div>
                        </div>
                        <div className={`estimatedclosingfee-tips-card-switch ${isMixed ? 'mixed-status' : ''}`}>
                            <Tooltip 
                                overlayClassName="estimatedclosingfee-tips-card-switch-tooltip" 
                                placement="bottom" 
                                title={tooltipText}
                            >
                                <Switch checked={useLodestar || isMixed} disabled={disabled} onChange={handleSwitchChange} />
                            </Tooltip>
                            Use Lodestar
                        </div>
                    </div>
                    {
                        (useLodestar || isMixed) && (
                            <div 
                                className="estimatedclosingfee-tips-card-learn-more"
                                onClick={() => setIsLearnMoreOpen(true)}
                            >
                                <span>Learn how Lodestar works</span>
                                <div className="estimatedclosingfee-tips-card-learn-more-icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="24" viewBox="0 0 25 24" fill="none">
                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M11.0607 6.18934L10 7.25L14.5956 11.8456L10.0018 16.4393L11.0625 17.5L15.6562 12.9063L15.6569 12.9069L16.7175 11.8462L16.7169 11.8456L16.7194 11.8431L15.6587 10.7825L15.6563 10.7849L11.0607 6.18934Z" fill="#325CEB"/>
                                    </svg>
                                </div>
                            </div>
                        )
                    }
                </div>
            </div>
            <Modal
                title="How Lodestar integration works"
                className='lodestar-integration-info-modal'
                styles={{ mask: { backgroundColor: 'rgba(0, 0, 0, 0.75)' } }}
                open={isLearnMoreOpen}
                onCancel={() => setIsLearnMoreOpen(false)}
                footer={null}
                width={410}
                centered
            >
                <div className='lodestar-integration-info-modal-title'>Lodestar simplifies fee accuracy by pulling real-time data from trusted title and government sources—auto-updating every time you run a rate quote.</div>
                <div className="lodestar-integration-info-modal-section">
                    <div className='lodestar-integration-info-modal-section-title'>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none">
                            <rect y="0.5" width="20" height="20" rx="10" fill="#2E605A"/>
                            <rect x="14.3477" y="6.38232" width="2.35294" height="10.5882" transform="rotate(45 14.3477 6.38232)" fill="white"/>
                            <rect x="3.5293" y="10.542" width="2.35294" height="7.05882" transform="rotate(-45 3.5293 10.542)" fill="white"/>
                        </svg>
                        <div>Automatically filled by Lodestar:</div>
                    </div>
                    {providedByLodestar.map((text) => (
                        <div className="lodestar-integration-info-modal-section-item" key={text}>{text}</div>
                    ))}
                </div>

                <div className="lodestar-integration-info-modal-section">
                    <div className='lodestar-integration-info-modal-section-title'>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none">
                            <rect y="0.5" width="20" height="20" rx="10" fill="#CB8600"/>
                            <rect x="8.88867" y="4.94434" width="2.22222" height="7.77778" rx="1.11111" fill="white"/>
                            <rect x="11.1113" y="13.8333" width="2.22222" height="2.22222" rx="1.11111" transform="rotate(90 11.1113 13.8333)" fill="white"/>
                        </svg>
                        <div>Needs manual input:</div>
                    </div>
                    {manualInputSections.map((text) => (
                        <div className="lodestar-integration-info-modal-section-item" key={text}>{text}</div>
                    ))}
                </div>
            </Modal>
        </>
    );
};

export default LodestarTipsCard;
